import type { DataOption } from '@/common.types'
import type { UseControllerProps } from 'react-hook-form'

import React from 'react'
import { HelpCircleIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Label } from '@/shared/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

export default function RHFRadioCardsGroup({
	name,
	label,
	shouldUnregister,
	defaultValue,
	options,
	disabled,
	hint,
	description,
}: UseControllerProps & {
	label: string
	hint?: string
	description?: string
	disabled?: boolean
	options: (DataOption & {
		description?: string
	})[]
}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field }) => (
				<FormItem className='gap-0'>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<HelpCircleIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					{!!description && <FormDescription>{description}</FormDescription>}
					<RadioGroup
						onValueChange={field.onChange}
						defaultValue={field.value}
						className='mt-1.5 flex flex-col gap-2'
					>
						{options.map((option) => (
							<Label
								className='flex items-start gap-3 rounded-lg border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-input/30'
								key={option.value}
							>
								<FormControl>
									<RadioGroupItem
										value={option.value}
										disabled={disabled}
										className='data-[state=checked]:border-primary'
									/>
								</FormControl>

								<div className='grid gap-1 font-normal'>
									<div className='font-medium'>{option.label}</div>
									<div className='pr-2 text-sm leading-snug text-balance text-muted-foreground'>
										{option.description}
									</div>
								</div>
							</Label>
						))}
					</RadioGroup>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
